import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { getConfig } from '@/config';
import { hubClient } from '@tfpl/api-client';
import { permissions } from '@/store/modules/PositionModule';

export interface UserIdParam {
  userId?: number;
}

export interface User {
  uuid: string | null;
  slackAvatarUrl: string;
  fullName: string | null;
}

export interface DocumentsPayload {
  uuid: string;
  month: string;
}

@Module({ name: 'DocumentsModule', store, dynamic: true, namespaced: true })
export class DocumentsModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _documents: hubClient.DocumentDto[] = [];
  private _users: User[] = [];

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get documents(): hubClient.DocumentDto[] {
    return this._documents;
  }

  get users(): User[] {
    return this._users;
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }

  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  setDocuments(documents: hubClient.DocumentDto[]): void {
    this._documents = documents;
  }

  @Mutation
  clearData(): void {
    this._documents = [];
  }

  @Mutation
  setUsers(users: User[]): void {
    this._users = users;
  }

  @Mutation
  clearUsers(): void {
    this._users = [];
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchDocuments(payload: DocumentsPayload): Promise<void> {
    this.setLoading(true);
    this.clearData();
    const userUuid = payload.uuid;
    const month = payload.month;
    try {
      const { data: response } = await hubClient.V3DocumentsService.getManyV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        query: { limit: 999, offset: 0, userUuid, month },
        throwOnError: true,
      });
      this.setDocuments(response.data);
    } catch (err) {
      this.setError(true);
      console.log(err);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  public async fetchUsers(): Promise<void> {
    this.setLoading(true);

    try {
      const { data: response } = await hubClient.V3CollaboratorsService.getManyV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        query: {
          limit: 999,
          offset: 0,
          permissionSubject: permissions.HUB_DOCUMENTS.READ_OWN.subject,
          permissionAction: permissions.HUB_DOCUMENTS.READ_OWN.action,
        },
        throwOnError: true,
      });
      const users: User[] = [];
      for (const user of response.data) {
        users.push({
          uuid: user.uuid,
          fullName: `${user.firstName} ${user.lastName}`,
          slackAvatarUrl: user.slackAvatarUrl,
        });
      }
      this.setUsers(users);
    } catch (err) {
      this.setError(true);
      console.log(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(DocumentsModule);
